import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Image, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useLocation } from 'react-router-dom';

import CONSTANTS from 'src/store/constants';
import { authActions } from 'src/store/actions';
import { ErrorText, Button } from 'src/components';

const TMP_PWD_EXPIRATION_ERROR = 'Temporary password has expired and must be reset by an administrator.';

const RESEND_INVITATION_MSG =
  'The temporary password has expired. We have sent a new temporary password to your email. Please activate your account in the next 7 days.';

function Login({
  history,
  username: loginUsername,
  loginStatus,
  loginError,
  resendInvitationStatus,
  resendInvitationError,
  challengeName,
  passwordReset,
  login,
  resendInvitation,
}) {
  const defaultValues = {
    username: '',
    password: '',
  };
  const location = useLocation();

  const { handleSubmit, errors, control } = useForm({ defaultValues });

  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = (data) => {
    setErrorMessage('');
    const { username, password } = data;
    login(username.trim(), password);
  };

  useEffect(() => {
    if (challengeName) {
      history.push('/password-challenge');
    }
  }, [challengeName, history]);

  useEffect(() => {
    if (loginStatus === CONSTANTS.API.SUCCEEDED) {
      if (location && location.state && location.state.from) {
        window.history.replaceState({}, '');
        window.location.replace(`${location.state.from}${location.state.query || ''}`);
      } else {
        window.location.replace('/');
      }
    } else if (loginStatus === CONSTANTS.API.FAILED) {
      if (TMP_PWD_EXPIRATION_ERROR === loginError) {
        // Password has expired. Resend invitation
        // and flash a message for user to check the
        // email
        resendInvitation(loginUsername);
      } else {
        setErrorMessage(loginError);
      }
    }
  }, [loginStatus, loginError, resendInvitation, loginUsername, location]);

  useEffect(() => {
    if (resendInvitationStatus === CONSTANTS.API.SUCCEEDED) {
      setErrorMessage(RESEND_INVITATION_MSG);
    } else if (resendInvitationStatus === CONSTANTS.API.FAILED) {
      setErrorMessage(resendInvitationError);
    }
  }, [resendInvitationStatus, resendInvitationError]);

  useEffect(() => {
    if (passwordReset) {
      history.push('/change-password');
    }
  }, [passwordReset, history]);

  return (
    <Div
      minW="100vw"
      w="100%"
      minH="100vh"
      h="100%"
      d="flex"
      justify="center"
      align="center"
      p={{ x: '1rem', y: '4rem' }}
      pos="relative"
    >
      <Image
        src="/static/login-back.png"
        srcSet="/static/login-back@2x.png 2x,/static/login-back@3x.png 3x,"
        alt="background"
        w="100vw"
        h="100vh"
        pos="absolute"
        top="0"
        style={{
          objectFit: 'cover',
        }}
      />
      <Div
        w="420px"
        d="flex"
        flexDirection="column"
        align="center"
        shadow="5"
        p={{ x: '2rem', t: '3rem', b: '1rem' }}
        rounded="sm"
        bg="black75"
        zIndex="10"
        style={{
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container>
          <Div d="flex" justify="center" align="center">
            <Image src="/static/on-point-login.svg" w="108px" h="78px" />
          </Div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ y: '1rem' }} d="flex" justify="center">
              <Col>
                <Div d="flex" align="center">
                  <ErrorText shake={errorMessage !== ''}>{errorMessage}</ErrorText>
                </Div>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="username"
                  as={Input}
                  placeholder="Username / Email"
                  control={control}
                  rules={{
                    required: 'username or email is required',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption" p={{ x: '0.25rem' }}>
                  <ErrorMessage errors={errors} name="username" />
                </Text>
              </Col>
            </Row>
            <Row p={{ t: '0', b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="password"
                  as={Input}
                  control={control}
                  type="password"
                  placeholder="Password"
                  rules={{
                    required: 'Password is required',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  h="2.4rem"
                  p={{ y: '1em', x: '1.5em' }}
                  textSize="input"
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption" p={{ x: '0.25rem' }}>
                  <ErrorMessage errors={errors} name="password" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Div d="flex" justify="flex-end">
                  <Button
                    buttonStyle="login"
                    onClick={(event) => {
                      handleSubmit(onSubmit)(event);
                    }}
                    onKeyDown={(event) => {
                      event.preventDefault();
                      if (event.keyCode === 13) {
                        handleSubmit(onSubmit)(event);
                      }
                    }}
                    isLoading={loginStatus === CONSTANTS.API.STARTED}
                  >
                    Login
                  </Button>
                </Div>
              </Col>
            </Row>
          </form>
          <Div d="flex" justify="space-between" align="center">
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Image w="90px" h="2rem" src="/static/powered-by-pinpoint-white.svg" />
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  window.open('https://pinpointpredictive.com/tos/', '_blank') ||
                    window.location.replace('https://pinpointpredictive.com/tos/');
                }}
              >
                Terms of Service
              </Text>
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  history.push('/forgot-password');
                }}
              >
                Forgot password
              </Text>
            </Div>
          </Div>
        </Container>
      </Div>
    </Div>
  );
}

const mapStateToProps = ({ auth }) => ({
  ...auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (...params) => dispatch(authActions.login(...params)),
  resendInvitation: (...params) => dispatch(authActions.resendInvitation(...params)),
});

Login.propTypes = {
  username: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  loginStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  loginError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resendInvitationStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resendInvitationError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  challengeName: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  passwordReset: PropTypes.oneOfType([PropTypes.bool, undefined]).isRequired,
  login: PropTypes.func.isRequired,
  resendInvitation: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
